/**
 * CRTHeader V2 — Clean header with subtle glow and optional typing effect.
 * Less aggressive than V1 — elegant, not chaotic.
 */

import { useState, useEffect, useMemo } from "react";

interface CRTHeaderProps {
  text: string;
  level?: 1 | 2 | 3;
  typing?: boolean;
  glitch?: boolean;
  className?: string;
}

export function CRTHeader({
  text,
  level = 1,
  typing = false,
  glitch = false,
  className = "",
}: CRTHeaderProps) {
  const [displayText, setDisplayText] = useState(typing ? "" : text);
  const [cursorVisible, setCursorVisible] = useState(typing);

  useEffect(() => {
    if (!typing) {
      setDisplayText(text);
      return;
    }

    let index = 0;
    setDisplayText("");

    const interval = setInterval(() => {
      if (index < text.length) {
        setDisplayText(text.slice(0, index + 1));
        index++;
      } else {
        clearInterval(interval);
        setTimeout(() => setCursorVisible(false), 2000);
      }
    }, 50);

    return () => clearInterval(interval);
  }, [text, typing]);

  const sizeClasses = {
    1: "text-[26px] md:text-[34px]",
    2: "text-[20px] md:text-[24px]",
    3: "text-[17px] md:text-[19px]",
  };

  const Tag = level === 1 ? "h1" : level === 2 ? "h2" : "h3";

  return (
    <Tag
      className={`
        ${sizeClasses[level]}
        bw-display
        text-[var(--bw-fg)]
        ${className}
      `}
      style={{
        textShadow: "0 0 24px color-mix(in srgb, var(--bw-accent) 35%, transparent)",
        animation: glitch ? "bw-neon-pulse 4s ease-in-out infinite" : undefined,
      }}
    >
      {displayText}
      {cursorVisible && (
        <span
          className="inline-block w-[2px] h-[0.8em] ml-1 align-middle"
          style={{
            backgroundColor: "var(--bw-accent)",
            boxShadow: "0 0 6px var(--bw-accent)",
            animation: "bw-neon-pulse 1s ease-in-out infinite",
          }}
        />
      )}
    </Tag>
  );
}
